import JSZip from 'jszip';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    UploadDICOMRequestBody,
    useUploadDICOM,
} from '~/backend/connector-api/connector-components';
import { DicomRoi } from '~/domain/dicom/dicomRoi';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { StructureSet } from '~/domain/patient/structureset';
import { getStructureSetRepository } from '~/domain/ServiceContainer';
import { environmentState } from '~/state/atoms/engineAtoms';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { handleError } from '~/utils/errors';
import {
    buildRegistrationFilename,
    buildRtDoseFilename,
    buildRtPlanFilename,
    buildStructureSetFilename,
} from '~/utils/io/files/dicomFilename';
import {
    downloadImageToDisk,
    saveBlobToFile,
} from '~/utils/io/files/downloadDicom';
import {
    buildOptimizationObjectivesCsv,
    buildOptimizationObjectivesFilename,
    buildOptimizationObjectivesJson,
} from '~/utils/io/files/optimizationObjectives';
import useSelectedPatient from '../patient/useSelectedPatient';

type SelectedStructureSetsWithStructures = {
    structureSet: StructureSet;
    selectedRois?: DicomRoi[];
};

/**
 * Extracts the DICOM dose and plan blobs from a DoseMatrix.
 */
const getDoseBlobs = (
    dose: DoseMatrix
): { doseBlob?: Blob; planBlob?: Blob } => ({
    doseBlob: dose.dicomDose.getAsDicomBlob(),
    planBlob: dose.plan?.dicom?.getAsDicomBlob(),
});

export const useDicomExport = () => {
    const { mutateAsync: uploadDICOM } = useUploadDICOM();
    const selectedPatient = useSelectedPatient();
    const [exporting, setExporting] = useState(false);
    const environment = useRecoilValue(environmentState);
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);

    /**
     * Extracts selected structures from a StructureSet as a DICOM Blob.
     */
    const structureSetToBlob = (
        structureSet: StructureSet,
        selectedRois?: DicomRoi[],
        exportToMonaco?: boolean
    ): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        environment.saveChanges();
        if (selectedRois) {
            return structureSet.exportWith(
                selectedRois.map((roi) => roi.roiNumber),
                exportToMonaco ? 'ISO_IR 100' : undefined
            );
        }
        return structureSet.export();
    };

    /**
     * Converts a StructureSet to a Monaco DIR file Blob.
     *
     * This function exports the structure set in a format compatible with Monaco,
     * allowing for easy sharing and collaboration.
     *
     * @param {StructureSet} structureSet - The structure set to be exported.
     * @returns {Blob} A Blob representing the exported structure set in Monaco format.
     */
    const monacoStructureSetToBlob = (structureSet: StructureSet): Blob => {
        if (!environment) {
            throw new Error('Internal error, Environment not found');
        }
        environment.saveChanges();
        return structureSet.exportAsMonacoFile(
            selectedPatient?.dataSet.PatientName ?? 'NO_NAME'
        );
    };

    const uploadDicomBlob = async (
        blob: Blob,
        filename: string,
        targetName: string
    ): Promise<void> => {
        const formData = new FormData();
        formData.append('files', blob, filename);
        await uploadDICOM({
            body: formData as unknown as UploadDICOMRequestBody,
            pathParams: { target: targetName },
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    };

    /**
     * Downloads a DICOM Image as a ZIP file.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * combines them into a ZIP archive, and downloads it.
     *
     * @param {Image3D} image - The image to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadImage = async (image: Image3D): Promise<void> => {
        setExporting(true);
        await downloadImageToDisk(image);
        setExporting(false);
    };

    /**
     * Exports a DICOM Image by uploading all slices.
     *
     * This function extracts all DICOM slices from an Image3D object,
     * and uploads them individually to the server.
     *
     * @param {Image3D} image - The image to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportImage = async (
        image: Image3D,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);

            const blobs = Array.from(image.getSlicesAsDicomBlobs());

            for (let i = 0; i < blobs.length; i++) {
                const blob = blobs[i];
                await uploadDicomBlob(
                    blob,
                    `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`,
                    targetName
                );
            }

            image.setExported();

            enqueueSnackbar('DICOM image files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during image export:', error);
            enqueueSnackbar('Failed to upload image files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a DICOM Structure Set file.
     *
     * This function exports a Structure Set either with a subset of selected ROIs
     * or the entire structure set, then saves it as a `.dcm` file.
     *
     * @param {StructureSet} structureSet - The structure set to be exported.
     * @param {DicomRoi[]} [selectedRois] - An optional array of ROIs to include in the export.
     *                                  If omitted, the entire structure set is exported.
     * @returns {Promise<void>} A promise that resolves when the export process completes.
     */
    const handleDownloadStructureSet = async (
        structureSetUid: string,
        selectedRois?: DicomRoi[]
    ): Promise<void> => {
        try {
            setExporting(true);

            const repository = getStructureSetRepository();
            const groundTruthStructureSet =
                repository.getGroundTruth(structureSetUid);
            const structureSetBlob = structureSetToBlob(
                groundTruthStructureSet,
                selectedRois
            );

            if (!structureSetBlob) {
                throw new Error('Failed to generate Structure Set Blob');
            }

            saveBlobToFile(
                structureSetBlob,
                buildStructureSetFilename(
                    groundTruthStructureSet.structureSetLabel,
                    structureSetUid
                )
            );
        } catch (error) {
            handleError(error, 'Exporting files failed, please try again.');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a Structure Set as a DICOM file and uploads it.
     *
     * This function converts a selected subset of ROIs from the structure set into a DICOM file,
     * packages it as FormData, and uploads it to the server.
     *
     * @param {StructureSet} structureSet - The structure set containing the ROIs.
     * @param {DicomRoi[]} selectedRois - The list of ROIs to include in the export.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportStructureSet = async (
        structureSet: StructureSet,
        selectedRois: DicomRoi[],
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);

            const structureSetBlob = structureSetToBlob(
                structureSet,
                selectedRois
            );

            if (!structureSetBlob) {
                throw new Error('Failed to generate Structure Set Blob');
            }

            const formData = new FormData();
            formData.append(
                'files',
                structureSetBlob,
                buildStructureSetFilename(
                    structureSet.structureSetLabel,
                    structureSet.dicomUid
                )
            );

            await uploadDICOM({
                body: formData as unknown as UploadDICOMRequestBody,
                pathParams: { target: targetName },
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            structureSet.setExported();

            setHasUnsavedChanges(false);
            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a Dose Matrix as DICOM files and uploads them.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix,
     * packages them as FormData, and uploads them to the server.
     *
     * @param {DoseMatrix} dose - The dose matrix to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportDose = async (
        dose: DoseMatrix,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (planBlob) {
                await uploadDicomBlob(
                    planBlob,
                    buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                    targetName
                );
            }
            if (doseBlob) {
                await uploadDicomBlob(
                    doseBlob,
                    buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                    targetName
                );
            }

            dose.setExported();
            dose.plan?.setExported();

            enqueueSnackbar('DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during export:', error);
            enqueueSnackbar('Failed to upload DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a Dose Matrix as DICOM files.
     *
     * This function extracts the DICOM dose and plan from a Dose Matrix
     * and saves them as `.dcm` files.
     *
     * @param {DoseMatrix} dose - The dose matrix to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadDose = async (dose: DoseMatrix): Promise<void> => {
        try {
            setExporting(true);
            const { doseBlob, planBlob } = getDoseBlobs(dose);
            if (doseBlob) {
                saveBlobToFile(
                    doseBlob,
                    buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
            if (planBlob) {
                saveBlobToFile(
                    planBlob,
                    buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid)
                );
            }
        } catch (error) {
            handleError(
                error,
                'Downloading dose files failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads a DICOM Registration file.
     *
     * @param {RigidRegistration} registration - The registration to be downloaded.
     * @returns {Promise<void>} A promise that resolves when the download completes.
     */
    const handleDownloadRegistration = async (
        registration: RigidRegistration
    ): Promise<void> => {
        try {
            setExporting(true);
            registration.syncTransformationToDataset();
            const blob = registration.dicomParent.getAsDicomBlob();
            const label = registration.contentLabel || registration.dicomUid;
            saveBlobToFile(blob, buildRegistrationFilename(label));
        } catch (error) {
            handleError(
                error,
                'Downloading registration file failed, please try again.'
            );
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports a DICOM Registration file and uploads it.
     *
     * @param {RigidRegistration} registration - The registration to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload completes.
     */
    const handleExportRegistration = async (
        registration: RigidRegistration,
        targetName: string
    ): Promise<void> => {
        try {
            setExporting(true);
            registration.syncTransformationToDataset();
            const blob = registration.dicomParent.getAsDicomBlob();
            const label = registration.contentLabel || registration.dicomUid;
            await uploadDicomBlob(
                blob,
                buildRegistrationFilename(label),
                targetName
            );
            registration.setExported();
            enqueueSnackbar('DICOM registration file uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during registration export:', error);
            enqueueSnackbar('Failed to upload registration file', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };

    /**
     * Downloads multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` as a batch.
     *
     * If there's only one item, it will download it directly.
     * If there are multiple items, it will combine them into a ZIP archive.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects containing a StructureSet and its selected ROIs.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be downloaded.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be downloaded.
     * @returns {Promise<void>} A promise that resolves when all downloads are completed.
     */
    const handleDownloadBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        exportToMonaco: boolean = false,
        selectedRegistrations: RigidRegistration[] = []
    ): Promise<void> => {
        try {
            setExporting(true);
            const totalItems =
                selectedStructureSets.length +
                selectedDoses.length +
                selectedImages.length +
                selectedRegistrations.length;

            // Single-dose-with-objectives must go through the zip path
            // because the JSON sidecar would otherwise trigger a separate
            // browser download alongside the .dcm files (and some browsers
            // block multiple consecutive saves).
            const singleDoseHasObjectives =
                selectedDoses.length === 1 &&
                (selectedDoses[0].optimizationObjectives?.length ?? 0) > 0;

            if (totalItems === 1 && !singleDoseHasObjectives) {
                if (selectedImages.length === 1) {
                    return await handleDownloadImage(selectedImages[0]);
                } else if (
                    selectedStructureSets.length === 1 &&
                    !exportToMonaco
                ) {
                    const { structureSet, selectedRois } =
                        selectedStructureSets[0];
                    return await handleDownloadStructureSet(
                        structureSet.dicomUid,
                        selectedRois
                    );
                } else if (selectedDoses.length === 1) {
                    return await handleDownloadDose(selectedDoses[0]);
                } else if (selectedRegistrations.length === 1) {
                    return await handleDownloadRegistration(
                        selectedRegistrations[0]
                    );
                }
            }

            const timestamp = new Date()
                .toISOString()
                .replace(/[:.]/g, '-')
                .slice(0, -5);
            const filename = `dicom-export-${timestamp}`;

            const zip = new JSZip();

            for (const image of selectedImages) {
                const dir = `${image.seriesMetadata.modality}_${image.dicomUid}`;
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                blobs.forEach((blob, index) => {
                    zip.file(`${dir}/${index}.dcm`, blob);
                });
            }

            for (const {
                structureSet,
                selectedRois,
            } of selectedStructureSets) {
                const blob = structureSetToBlob(
                    structureSet,
                    selectedRois,
                    exportToMonaco
                );
                if (blob) {
                    zip.file(
                        buildStructureSetFilename(
                            structureSet.structureSetLabel,
                            structureSet.dicomUid
                        ),
                        blob
                    );
                }

                if (exportToMonaco) {
                    const monacoBlob = monacoStructureSetToBlob(structureSet);
                    if (monacoBlob) {
                        const monacoFilename = buildStructureSetFilename(
                            structureSet.structureSetLabel,
                            structureSet.dicomUid
                        ).replace(/\.dcm$/, '.dir');
                        zip.file(monacoFilename, monacoBlob);
                    }
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (doseBlob) {
                    zip.file(
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        doseBlob
                    );
                }
                if (planBlob) {
                    zip.file(
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        planBlob
                    );
                }
                if (
                    dose.optimizationObjectives &&
                    dose.optimizationObjectives.length > 0
                ) {
                    const json = buildOptimizationObjectivesJson(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid
                        ),
                        JSON.stringify(json, null, 2)
                    );
                    const csv = buildOptimizationObjectivesCsv(
                        dose.optimizationObjectives
                    );
                    zip.file(
                        buildOptimizationObjectivesFilename(
                            dose.plan?.label,
                            dose.dicomUid,
                            'csv'
                        ),
                        csv
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                zip.file(buildRegistrationFilename(label), blob);
            }

            // Generate and download the ZIP file
            const zipBlob = await zip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
            });
            saveBlobToFile(zipBlob, `${filename}.zip`);
        } catch (error) {
            handleError(error, 'Failed to download DICOM files');
        } finally {
            setExporting(false);
        }
    };

    /**
     * Exports multiple selected `StructureSet`, `DoseMatrix`, and `Image3D` in a single batch upload.
     *
     * This function processes multiple objects,
     * extracts the corresponding DICOM files, and uploads them all in separate API requests.
     *
     * @param {SelectedStructureSetsWithStructures[]} selectedStructureSets -
     *        An array of objects containing a StructureSet and its selected ROIs.
     * @param {DoseMatrix[]} selectedDoses -
     *        An array of DoseMatrix objects representing dose distributions to be exported.
     * @param {Image3D[]} selectedImages -
     *        An array of Image3D objects representing images to be exported.
     * @returns {Promise<void>} A promise that resolves when the upload process is completed.
     */
    const handleExportBatch = async (
        selectedImages: Image3D[] = [],
        selectedStructureSets: SelectedStructureSetsWithStructures[],
        selectedDoses: DoseMatrix[],
        targetName: string,
        selectedRegistrations: RigidRegistration[] = []
    ) => {
        try {
            setExporting(true);

            for (const image of selectedImages) {
                const blobs = Array.from(image.getSlicesAsDicomBlobs());
                for (let i = 0; i < blobs.length; i++) {
                    const blob = blobs[i];
                    await uploadDicomBlob(
                        blob,
                        `${image.seriesMetadata.modality}_${image.dicomUid}_${i}.dcm`,
                        targetName
                    );
                }
            }

            for (const structureSetWithStructures of selectedStructureSets) {
                const blob = structureSetToBlob(
                    structureSetWithStructures.structureSet,
                    structureSetWithStructures.selectedRois
                );
                if (blob) {
                    await uploadDicomBlob(
                        blob,
                        buildStructureSetFilename(
                            structureSetWithStructures.structureSet
                                .structureSetLabel,
                            structureSetWithStructures.structureSet.dicomUid
                        ),
                        targetName
                    );
                }
            }

            for (const dose of selectedDoses) {
                const { doseBlob, planBlob } = getDoseBlobs(dose);
                if (planBlob) {
                    await uploadDicomBlob(
                        planBlob,
                        buildRtPlanFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
                if (doseBlob) {
                    await uploadDicomBlob(
                        doseBlob,
                        buildRtDoseFilename(dose.plan?.label ?? dose.dicomUid),
                        targetName
                    );
                }
            }

            for (const registration of selectedRegistrations) {
                registration.syncTransformationToDataset();
                const blob = registration.dicomParent.getAsDicomBlob();
                const label =
                    registration.contentLabel || registration.dicomUid;
                await uploadDicomBlob(
                    blob,
                    buildRegistrationFilename(label),
                    targetName
                );
            }

            selectedImages.forEach((image) => {
                image.setExported();
            });

            selectedStructureSets.forEach(({ structureSet }) => {
                structureSet.setExported();
            });

            selectedDoses.forEach((dose) => {
                dose.setExported();
                dose.plan?.setExported();
            });

            selectedRegistrations.forEach((registration) => {
                registration.setExported();
            });

            setHasUnsavedChanges(false);
            enqueueSnackbar('All selected DICOM files uploaded successfully', {
                variant: 'success',
            });
        } catch (error) {
            console.error('Error during batch export:', error);
            enqueueSnackbar('Failed to upload batch DICOM files', {
                variant: 'error',
            });
        } finally {
            setExporting(false);
        }
    };
    return {
        exporting,
        handleDownloadStructureSet,
        handleExportImage,
        handleExportStructureSet,
        handleExportDose,
        handleDownloadDose,
        handleDownloadRegistration,
        handleExportRegistration,
        handleDownloadBatch,
        handleExportBatch,
    };
};
